import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/utils/apiClient';
import { useAuth } from '@/contexts/AuthContext';

export interface PractitionerInfo {
  practitioner_id: string;
  full_name: string | null;
  email: string;
  referral_code: string;
  discount_percentage: number | null;
}

/**
 * Hook to fetch information about the practitioner a user is connected to
 */
export const usePractitionerInfo = () => {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery<PractitionerInfo>({
    queryKey: ['practitionerInfo', user?.id],
    queryFn: async () => {
      // apiClient.get() returns the JSON response directly (not wrapped in {data: ...})
      const response = await apiClient.get<PractitionerInfo>('/referrals/user/practitioner-info');
      return response;
    },
    enabled: !!user,
    retry: 1, // Retry once in case of temporary failures
    retryDelay: 1000, // Wait 1 second before retrying
    staleTime: 30 * 1000, // Cache for only 30 seconds (shorter to pick up changes faster)
    gcTime: 60 * 1000, // Keep in cache for 1 minute
  });

  return {
    practitionerInfo: data,
    isLoading,
    error,
    refetch,
    hasConnection: !!data,
  };
};
